/**
 * The sync engine, wired to the app.
 *
 * Decides *when* to sync; `sync.ts` decides what that means. Kept apart
 * because the what is worth testing and the when is a pile of browser events.
 *
 * Four triggers, and the first two are the ones that matter:
 *
 *  * the browser reports it is back online — a van coming out of a tunnel,
 *  * the tab becomes visible again — a phone coming out of a pocket, which is
 *    the more reliable of the two, because `navigator.onLine` cheerfully
 *    reports online on one bar of 4G while every request times out,
 *  * a timer, while the tab is in front,
 *  * a person pressing the button.
 */

import {
  createContext,
  useCallback,
  useContext,
  useEffect,
  useMemo,
  useRef,
  useState,
  type ReactNode,
} from "react";

import { transport } from "./api";
import * as outbox from "./outbox";
import { wipe } from "./store";
import { type SyncResult, status as readStatus, syncNow } from "./sync";

const EVERY_MS = 2 * 60 * 1000;

export type SyncState = Awaited<ReturnType<typeof readStatus>>;

interface OfflineValue {
  state: SyncState | null;
  /** True while a sync is in flight, so the button can say so. */
  syncing: boolean;
  /** The last thing that happened, written for a person to read. */
  last: SyncResult | null;
  online: boolean;
  sync: (options?: { force?: boolean }) => Promise<void>;
  refresh: () => Promise<void>;
  acknowledgeRefused: () => Promise<void>;
  /** Forget everything. What signing out does — van iPads change hands. */
  forget: () => Promise<void>;
}

const OfflineContext = createContext<OfflineValue | null>(null);

export function OfflineProvider({ children, enabled }: { children: ReactNode; enabled: boolean }) {
  const [state, setState] = useState<SyncState | null>(null);
  const [syncing, setSyncing] = useState(false);
  const [last, setLast] = useState<SyncResult | null>(null);
  const [online, setOnline] = useState(() => navigator.onLine);

  // A ref, not state: two syncs at once would push the same queue twice, and
  // re-rendering on every guard check would be its own problem.
  const running = useRef(false);

  const refresh = useCallback(async () => {
    try {
      setState(await readStatus());
    } catch {
      // The store being unreadable is worth knowing about but not worth
      // blocking a screen over — private browsing, blocked site data.
      setState(null);
    }
  }, []);

  const sync = useCallback(
    async ({ force = false } = {}) => {
      if (!enabled || running.current) return;
      running.current = true;
      setSyncing(true);
      try {
        const result = await syncNow(transport, { force });
        setLast(result);
        setOnline(!result.offline);
      } finally {
        running.current = false;
        setSyncing(false);
        await refresh();
      }
    },
    [enabled, refresh],
  );

  // The queue changing is its own signal, separate from a sync landing: work
  // recorded with no signal has to show up on the status strip immediately.
  useEffect(() => {
    if (!enabled) return;
    return outbox.subscribe(() => void refresh());
  }, [enabled, refresh]);

  useEffect(() => {
    if (!enabled) {
      setState(null);
      return;
    }
    void sync();

    const onOnline = () => {
      setOnline(true);
      void sync();
    };
    const onOffline = () => setOnline(false);
    const onVisible = () => {
      if (document.visibilityState === "visible") void sync();
    };

    window.addEventListener("online", onOnline);
    window.addEventListener("offline", onOffline);
    document.addEventListener("visibilitychange", onVisible);
    const timer = window.setInterval(() => {
      if (document.visibilityState === "visible") void sync();
    }, EVERY_MS);

    return () => {
      window.removeEventListener("online", onOnline);
      window.removeEventListener("offline", onOffline);
      document.removeEventListener("visibilitychange", onVisible);
      window.clearInterval(timer);
    };
  }, [enabled, sync]);

  const acknowledgeRefused = useCallback(async () => {
    await outbox.acknowledgeRejected();
    await refresh();
  }, [refresh]);

  const forget = useCallback(async () => {
    await wipe();
    setState(null);
    setLast(null);
  }, []);

  const value = useMemo(
    () => ({ state, syncing, last, online, sync, refresh, acknowledgeRefused, forget }),
    [state, syncing, last, online, sync, refresh, acknowledgeRefused, forget],
  );

  return <OfflineContext.Provider value={value}>{children}</OfflineContext.Provider>;
}

export function useOffline(): OfflineValue {
  const value = useContext(OfflineContext);
  if (value === null) throw new Error("useOffline must be used inside an OfflineProvider");
  return value;
}
